import { useEffect, useState } from 'react'

/**
 * Renders its children only once the browser is idle (or after a short fallback), so heavy but
 * non-critical UI can be kept off the first paints. Used for the on-screen A4 prescription
 * preview: the editor should paint and become interactive first — especially on slow machines —
 * and the formatted preview can mount a beat later. (Quick Print renders from its own detached
 * root, so the print flow is unaffected by this deferral.)
 *
 * children may be a render function (deferring the work of building the subtree) or a node.
 */
export default function DeferredMount({ children, timeout = 800, fallbackMs = 200 }) {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const w = typeof window !== 'undefined' ? window : null
    if (w && typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(() => setReady(true), { timeout })
      return () => w.cancelIdleCallback?.(id)
    }
    const id = setTimeout(() => setReady(true), fallbackMs)
    return () => clearTimeout(id)
  }, [timeout, fallbackMs])

  if (!ready) {
    return null
  }
  return typeof children === 'function' ? children() : children
}
